import { SEO_DEFAULT_OG_IMAGE, SEO_SITE_ORIGIN } from "constants/seo";
import { getPublicAppBaseUrl, getPublicAssetUrl } from "./basePath";
import { getRuntimeConfig } from "./config";

export function getSeoSiteOrigin(): string {
  if (typeof window !== "undefined") {
    const fromRuntime = getPublicAppBaseUrl().replace(/\/+$/, "");
    if (fromRuntime) return fromRuntime;
  }
  const fromConfig = String(getRuntimeConfig("QRCARD_URL", "")).trim();
  if (fromConfig) return fromConfig.replace(/\/+$/, "");
  return SEO_SITE_ORIGIN;
}

export function getCanonicalUrl(pathname = ""): string {
  const origin = getSeoSiteOrigin();
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (path === "/" || path === "") return `${origin}/`;
  return `${origin}${path}`.replace(/([^:]\/)\/+/g, "$1");
}

export function getDefaultOgImageUrl(): string {
  if (typeof window !== "undefined") {
    return `${getSeoSiteOrigin()}${getPublicAssetUrl("/logo.png")}`.replace(
      /([^:]\/)\/+/g,
      "$1"
    );
  }
  return SEO_DEFAULT_OG_IMAGE;
}

const PRIVATE_ROUTE_PREFIXES = [
  "/login",
  "/register",
  "/dashboard",
  "/users",
  "/companies",
  "/packages",
  "/licenses",
  "/invoices",
  "/lookups",
  "/contactus",
  "/upgrade",
  "/forgotpassword",
  "/forgot-password",
  "/my-profile",
  "/company/",
  "/paymentResult",
  "/not-found",
] as const;

export function isPrivateAppRoute(pathname: string): boolean {
  const path = pathname.split("?")[0].split("#")[0];
  if (PRIVATE_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix))) {
    return true;
  }
  if (path === "/employees" || path === "/individuals") return true;
  if (/^\/employees\/[^/]+\/(edit|view|scans)(\/|$)/.test(path)) return true;
  if (/^\/employees\/new(\/|$)/.test(path)) return true;
  if (/^\/individuals\/\d+\/scans/.test(path)) return true;
  return false;
}

export function isPublicProfileRoute(pathname: string): boolean {
  const path = pathname.split("?")[0].split("#")[0];
  const match = path.match(/^\/(employees|individuals)\/([^/]+)/);
  if (!match) return false;
  const [, kind, segment] = match;
  if (segment === "new") return false;
  if (kind === "employees" && /\/(edit|view|scans)(\/|$)/.test(path)) return false;
  if (kind === "individuals" && /\/scans/.test(path)) return false;
  if (/^\d+$/.test(segment) && /\/(edit|view|scans)/.test(path)) return false;
  return true;
}

export function resolveProfileOgImage(image?: string | null): string {
  const raw = (image || "").trim();
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return getDefaultOgImageUrl();
}

export function buildProfileSeoTitle(
  name?: string | null,
  jobTitle?: string | null,
  company?: string | null
): string {
  const displayName = (name || "").trim() || "Digital Profile";
  const role = (jobTitle || "").trim();
  const org = (company || "").trim();
  if (role && org) return `${displayName} – ${role} at ${org}`;
  if (role) return `${displayName} – ${role}`;
  if (org) return `${displayName} – ${org}`;
  return displayName;
}

export function buildProfileSeoDescription(
  name?: string | null,
  jobTitle?: string | null,
  company?: string | null
): string {
  const displayName = (name || "").trim() || "This professional";
  const role = (jobTitle || "").trim();
  const org = (company || "").trim();
  if (role && org) {
    return `View ${displayName}'s verified digital business card — ${role} at ${org}. Scan the QR code or save contact details instantly.`;
  }
  if (role) {
    return `View ${displayName}'s verified digital business card — ${role}. Scan the QR code or save contact details instantly.`;
  }
  return `View ${displayName}'s verified digital business card on YourQcard. Scan the QR code or save contact details instantly.`;
}
